import type { IPayable } from '../../@types/Payable.js';
import type { ObservableLifecycleHooks } from '../createPayment.js';
import { createPaymentInfo } from '../createPaymentInfo.js';
import { createTasks } from '../createTask.js';
// import type { PaymentResponseApi } from './createPaymentResponseApi.js';

type PayableContextApi<TPayable> = [
  {
    getTotalPrice: () => number;
    count: () => number;
    getPayableItems: () => readonly TPayable[];
    getUnpaidCount: () => number;
    tasks: ReturnType<typeof createTasks>;
  },
  {
    setPaidCount: (paidCount: number) => void;
  },
];

/**
 * Creates a context object around the items in a payment process that is requested to be paid (or checked out).
 * The context object is used by the middlewares and can for example be used to retrieve information about total price, add thing that requires further user input etc.
 * The context object provides convenience functions around the payment and should not expose direct access to the items to be paid.
 *
 * @param payableItems - An array of payable items
 * @returns Api object with functions to interact with the payment process
 */
function createPayableContextApi<TPayable extends IPayable>(
  payableItems: readonly TPayable[]
): PayableContextApi<TPayable> {
  let paidPayableCount = 0;

  const getTotalPrice = (): number => payableItems.reduce((sum, payable) => sum + payable.price, 0);

  const count = (): number => payableItems.length;

  const getPayableItems = (): readonly TPayable[] => payableItems;

  const getUnpaidCount = (): number => payableItems.length - paidPayableCount;

  const context = {
    getTotalPrice,
    count,
    getPayableItems,
    getUnpaidCount,
    tasks: createTasks(),
  } as const;

  const setPaidCount = (paidCount: number): void => {
    paidPayableCount = paidCount;
  };

  const updateContext = {
    setPaidCount,
  } as const;

  return [context, updateContext] as const;
}

type MiddlewareContext<TPayable extends IPayable> = [
  {
    payment: ReturnType<typeof createPayableContextApi<TPayable>>['0'];
    observers: ObservableLifecycleHooks<TPayable>;
    paymentInfo: ReturnType<typeof createPaymentInfo>;
  },
  {
    payment: ReturnType<typeof createPayableContextApi<TPayable>>['1'];
  },
];

/**
 * Creates a context object around the items in a payment process that is requested to be paid (or checked out).
 *
 * @param payableItems - An array of payable items
 * @param observers - Observers to hook into the payment process lifecycle methods
 * @returns First item is the context and the second one is the update function
 */
export function createMiddlewareContextApi<TPayable extends IPayable>(
  payableItems: readonly TPayable[],
  observers: ObservableLifecycleHooks<TPayable>
): MiddlewareContext<TPayable> {
  const [payableContextApi, payableUpdateContextApi] = createPayableContextApi<TPayable>(payableItems);

  const context = {
    payment: payableContextApi,
    observers,
    paymentInfo: createPaymentInfo(),
  } as const;

  const updateContext = {
    payment: payableUpdateContextApi,
  } as const;

  return [context, updateContext] as const;
}

export type MiddlewareContextApi<TPayable extends IPayable> = ReturnType<
  typeof createMiddlewareContextApi<TPayable>
>[0];
